/*
  File: AgentPage.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the main component responsible for managing agents within the application.
  It provides functionalities for searching, filtering, sorting, viewing, adding, and editing agents.

  Components and Libraries:
  - React: Used for building UI components.
  - Header: Component for displaying page headers.
  - agentsIcon: Icon representing agents.
  - Ant Design Components: Button, Flex, Input, Radio, Space, Steps, Table, Tag, etc.

  State Management:
  - agents: Array to hold agent data fetched from the server.
  - searchText: String to store the search query entered by the user.
  - selectedAgent: Holds the selected agent for management.
  - manageAgentModalVisible: Controls the visibility of the manage agent modal.
  - size: Controls the size of the agent view (leader, table, graph, tree).
  - selectedLetters: Array to store selected letters for filtering agents by name.
  - filteredAndSortedAgents: Holds filtered and sorted agent data.
  - addAgentModelVisible: Controls the visibility of the add agent modal.
  - currentStep: Tracks the current step in the add agent wizard.
  - formData: Holds form data for adding a new agent.

  Effect Hooks:
  - useEffect: Fetches agents data from the server and applies filtering and sorting logic.

  Event Handlers:
  - handleAddAgent: Handles the addition of a new agent.
  - handleCancel: Handles the cancellation of adding or managing agents.
  - handleFinish: Handles completion of the add agent wizard.
  - handleFormDataChange: Handles changes in form data during the add agent process.
  - handleNext: Handles navigation to the next step in the add agent wizard.
  - handlePrev: Handles navigation to the previous step in the add agent wizard.
  - handleManageUser: Handles opening the manage agent modal.
  - handleLetterClick: Handles selection of letters for filtering agents.
  - handleSearch: Handles user input for searching agents.
  - createAgent: Handles the creation of a new agent.

  Render Logic:
  - Renders UI elements including search input, letters filter, agent view switcher, leader board, table, graph, tree, and modals for managing and adding agents.

  Steps and Wizards:
  - Implements a multi-step wizard for adding new agents, with each step represented by a component containing specific form fields.

  Data Transformation:
  - Utilizes functions like getFullName, getReferredBy, and getTotalReferral to transform agent data for display purposes.

  Integration:
  - Integrates with other components like LeaderBoard, AgentGraph, AgentsTree, ManageAgentModal, and AgentAdditionModal to provide a comprehensive agent management experience.
*/

import "./AgentPage.css";
import Header from "./Header";
import agentsIcon from "./icons/agents.gif";
import {
  Avatar,
  Button,
  Flex,
  Form,
  Input,
  message,
  Radio,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import service, { getAgents, getNameAbbrevation } from "./service/service";
import ManageAgentModal from "./AgentManageModel";
import React from "react";
import AgentGraph from "./AgentGraph";
import { useAuth } from "./context/AuthContext";
import LettersFilter from "./LetterFilter";
import LeaderBoard from "./LeaderBoard";
import Step_1_Information from "./Step_1_Information";
import { Agent } from "./types";
import BasicDetailsComponent from "./Step_2_BasicDetails";
import ReferralComponent from "./Step_3_Referrals";
import ReviewComponent from "./Step_4_Review";
import AgentsTree from "./AgentsTree";
import AgentAdditionMoal from "./AgentNewModal";
import AgentViewSwitcher from "./AgentsViewSwitcher";
import AgentSummary from "./AgentSummary";

function buildTree(agents: Agent[]): Agent | null {
  const rootAgent = agents.find((agent) => !agent.referred_by_id);

  if (!rootAgent) {
    // message.error("No root element found.");
    return null;
  }

  function buildNode(agent: Agent): Agent {
    const node = { ...agent };
    const referrals = agents.filter(
      (referral) => referral.referred_by_id === agent.id
    );
    if (referrals.length > 0) {
      node.agent_referrals = referrals.map((referral) => buildNode(referral));
    }
    return node;
  }

  return buildNode(rootAgent);
}

const AgentPage = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [agents, setAgents] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);
  const [manageAgentModalVisible, setManageAgentModalVisible] = useState(false);
  const [size, setSize] = useState<string>("leader"); // default is 'middle'
  const [selectedLetters, setSelectedLetters] = useState<string[]>([]);
  const [filteredAndSortedAgents, setFilteredAndSortedAgents] = useState<any[]>(
    []
  );
  const [addAgentModelVisible, setAddAgentModelVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { user, setAgent } = useAuth();

  const existingEmails: string[] = agents.map((agent) => agent.email);
  const existingPhoneNumbers: string[] = agents.map(
    (agent) => agent.phone_number
  );

  useEffect(() => {
    const fetchAgents = async () => {
      setLoading(true);
      try {
        const response = await getAgents();
        setAgents(response); // Update users state with fetched data
      } catch (error) {
        message.error(`Error fetching users:" ${error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  useEffect(() => {
    const filteredAgents = agents
      .filter((agent) =>
        agent.first_name?.toLowerCase().includes(searchText.toLowerCase())
      )
      .filter(filterAgentByLetter)
      .sort((a, b) => {
        const referralsA = a.referrals ?? [];
        const referralsB = b.referrals ?? [];
        return referralsB.length - referralsA.length;
      });

    setFilteredAndSortedAgents(filteredAgents);
  }, [agents, searchText, selectedLetters]);

  const handleAddAgentClick = () => {
    setCurrentStep(0);
    setAddAgentModelVisible(true);
    const userSkipPreferences = JSON.parse(
      localStorage.getItem("userSkipPreferences") || "{}"
    );
    const userId = user?.id;
    if (userId) {
      const skipPreferenceForUser = userSkipPreferences[userId];
      if (skipPreferenceForUser) {
        setCurrentStep(1);
      }
    }
  };

  const handleManageAgentModalClick = (agent: any) => {
    setSelectedAgent(agent);
    patchAgentFormData.first_name = agent?.first_name;
    patchAgentFormData.last_name = agent?.last_name || "";
    patchAgentFormData.email = agent?.email || "";
    patchAgentFormData.phone_number = agent?.phone_number || "";
    patchAgentFormData.status = agent?.status;
    setManageAgentModalVisible(true);
  };

  const onHandleAddAgentModalCancel = () => {
    setAddAgentModelVisible(false);
    for (const key in createAgentFormData) {
      if (Object.hasOwnProperty.call(createAgentFormData, key)) {
        createAgentFormData[key] = "";
      }
    }
  };
  const handlePatchAgentModalCancel = () => {
    setManageAgentModalVisible(false);
    for (const key in patchAgentFormData) {
      if (Object.hasOwnProperty.call(patchAgentFormData, key)) {
        patchAgentFormData[key] = "";
      }
    }
  };

  const [createAgentFormData, setCreateAgentFormData] = useState<any>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    hasAccess: false,
    referringAgent: null,
  });

  const [patchAgentFormData, setPatchAgentFormdata] = useState<any>({});

  const isCreateAgentFormDataValid = (formData: any) => {
    for (const key in formData) {
      if (key !== "hasAccess" && !formData[key]) {
        return false;
      }
    }
    return true;
  };

  const isPatchAgentFormDataValid = (formData: any) => {
    for (const key in formData) {
      if (!formData[key]) {
        return false;
      }
    }
    return true;
  };

  const handleCreateFormDataChange = (updatedData: any) => {
    setCreateAgentFormData({ ...createAgentFormData, ...updatedData });
  };

  const handlePatchFormDataChange = (updatedData: any) => {
    setPatchAgentFormdata({ ...patchAgentFormData, ...updatedData });
  };

  const filterAgentByLetter = (agent: any) => {
    if (!agent || !agent.first_name) return false; // Add null check here
    if (selectedLetters.length === 0) return true;
    return selectedLetters.some((letter) =>
      agent.first_name.toLowerCase().startsWith(letter.toLowerCase())
    );
  };

  const filteredAgents = agents
    ? agents.filter(
        (agent) =>
          agent.first_name &&
          agent.first_name.toLowerCase().includes(searchText.toLowerCase()) &&
          filterAgentByLetter(agent)
      )
    : [];

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <Tag color={text === "active" ? "green" : "red"}>
          {text.toLocaleUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, record: Agent) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            style={{ height: "45px", width: "45px", marginRight: "10px" }}
          >
            {getNameAbbrevation(record.first_name, record.last_name)}
          </Avatar>
          {getFullName(record)}
        </div>
      ),
      width: "auto", // Set width to auto
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "auto", // Set width to auto
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
    },

    {
      title: "Joining Date",
      dataIndex: "referred_date",
    },
    {
      title: "Referred By",
      dataIndex: "referred_by",
      render: (text: string, record: Agent) => getReferredBy(record),
    },
    {
      title: "My Passive Income Network",
      dataIndex: "referrals",
      render: (text: string, record: Agent) => getTotalReferral(record),
    },
  ];

  const createAgentSteps = [
    {
      title: "Information",
      content: <Step_1_Information />,
    },
    {
      title: "Basic Details",
      content: (
        <BasicDetailsComponent
          existingEmails={existingEmails}
          existingPhones={existingPhoneNumbers}
          formData={createAgentFormData}
          onFormDataChange={handleCreateFormDataChange}
        />
      ),
    },
    {
      title: "Referral",
      content: (
        <ReferralComponent
          existingAgents={agents}
          formData={createAgentFormData}
          onFormDataChange={handleCreateFormDataChange}
        />
      ),
    },
    {
      title: "Review",
      content: <ReviewComponent {...createAgentFormData} />,
    },
  ];

  const onHandleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const onHandlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const getFullName = (agent: Agent) =>
    `${agent.first_name} ${agent.last_name}`;

  const getReferredBy = (agent: Agent) => {
    if (!agent.referred_by) {
      return "House Account";
    }
    return `${agent.referred_by.first_name} ${agent.referred_by.last_name}`;
  };

  const getTotalReferral = (agent: Agent) =>
    agent.agent_referrals ? agent.agent_referrals.length : 0;

  const sortedAgents = filteredAgents.slice().sort((a, b) => {
    const referralsA = a.referrals ?? [];
    const referralsB = b.referrals ?? [];
    return referralsB.length - referralsA.length;
  });

  const countUsersByLetter = (letter: string) => {
    return agents.filter(
      (agent) =>
        agent.first_name &&
        agent.first_name.toLowerCase().startsWith(letter.toLowerCase())
    ).length;
  };

  const handleLetterClick = (letter: string) => {
    if (selectedLetters.includes(letter)) {
      setSelectedLetters(selectedLetters.filter((l) => l !== letter));
    } else {
      setSelectedLetters([...selectedLetters, letter]);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const onCreateAgent = async () => {
    try {
      if (createAgentFormData.hasAccess == "") {
        createAgentFormData.hasAccess = false;
      }
      const response = await service.createAgent(
        createAgentFormData.email,
        "pass",
        createAgentFormData.phone,
        createAgentFormData.firstName,
        createAgentFormData.lastName,
        createAgentFormData.hasAccess,
        createAgentFormData.referringAgent.id
      );
      const fetchAgents = async () => {
        setLoading(true);
        try {
          const response = await getAgents();
          setAgents(response); // Update users state with fetched data
        } catch (error) {
          message.error(`Error fetching users:" ${error}`);
        } finally {
          setLoading(false);
        }
      };

      fetchAgents();

      setAddAgentModelVisible(false);
      setCreateAgentFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        hasAccess: false,
        referringAgent: null,
      });
      setCurrentStep(0);
      await setAgent(user?.email);
    } catch (error) {
      message.error(`Error creating agent. ${JSON.stringify(error)}`);
    }
  };

  const onPatchAgent = async () => {
    try {
      const updatedData: Record<string, any> = {};
      for (const key in patchAgentFormData) {
        if (Object.hasOwnProperty.call(patchAgentFormData, key)) {
          if (patchAgentFormData[key] !== selectedAgent[key]) {
            updatedData[key] = patchAgentFormData[key];
          }
        }
      }
      const response = await service.patchAgent(selectedAgent.id, updatedData);
      const updatedAgents = agents.map((agent) => {
        if (agent.id === selectedAgent.id) {
          return { ...agent, ...updatedData };
        }
        return agent;
      });
      setAgents(updatedAgents);
      const updatedFilteredAgents = filteredAgents.map((agent) => {
        if (agent.id === selectedAgent.id) {
          return { ...agent, ...updatedData };
        }
        return agent;
      });
      setFilteredAndSortedAgents(updatedFilteredAgents);
      setPatchAgentFormdata({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        status: "active",
      });
      setManageAgentModalVisible(false);
      message.success("Successfully updated agent details");
      await setAgent(user?.email);
    } catch (error) {
      message.error(`Error patching agent. ${JSON.stringify(error)}`);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <Header
          header="Agents"
          subheader="View and manage all agents, see the leaderboard, or check out the network hierarchy."
          icon={agentsIcon}
        />

        <div style={{ background: "black", padding: "10px" }}>
          <div className="search-container">
            <Input
              placeholder="Search..."
              allowClear
              size="large"
              value={searchText}
              onChange={handleSearch}
            />
          </div>

          <LettersFilter
            selectedLetters={selectedLetters}
            handleLetterClick={handleLetterClick}
            countUsersByLetter={countUsersByLetter}
          />
          <div style={{ display: "flex", padding: "10px 0px" }}>
            <div style={{ flex: 2 }}>
              <b>{filteredAgents.length}</b> users found
            </div>
            <div style={{ flex: 1 }}>
              <Flex gap="small" style={{ justifyContent: "flex-end" }}>
                <Button type="primary" block onClick={handleAddAgentClick}>
                  <span style={{ fontWeight: 700 }}>New Agent</span>
                </Button>
              </Flex>
            </div>
          </div>
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Spin tip="Loading agents, please wait" size="large" />
          </div>
        ) : (
          <div>
            <div
              style={{
                marginTop: "0px",
                display: "Flex",
                alignContent: "center",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#000",
                padding: "10px",
              }}
            >
              <AgentViewSwitcher value={size} onChange={setSize} />
            </div>

            {size == "leader" && (
              <LeaderBoard
                sortedAgents={sortedAgents}
                handleManageAgentModalClick={handleManageAgentModalClick}
              />
            )}
            {size == "table" && (
              <div
                style={{
                  height: "1000px",
                  overflow: "auto",
                  background: "#000",
                  paddingTop: "40px",
                }}
              >
                <Table
                  dataSource={sortedAgents}
                  columns={columns}
                  pagination={false} // Disable pagination
                  scroll={{ x: "max-content" }}
                  rowKey={(record) => record.id}
                  rowClassName={() => "black-row"}
                  className="custom-table-header"
                />
              </div>
            )}
            {size == "graph" && (
              <div
                style={{
                  marginTop: "50px",
                  height: "1000px",
                  width: "1000px",
                  overflow: "auto",
                }}
              >
                <AgentGraph data={sortedAgents}></AgentGraph>
              </div>
            )}
            {size == "tree" && (
              <div
                style={{
                  height: "1000px",
                  width: "1000px",
                  overflow: "auto",
                  background: "#000",
                }}
              >
                <AgentsTree agent={buildTree(sortedAgents)} />
              </div>
            )}
          </div>
        )}
      </div>
      <ManageAgentModal
        agent={selectedAgent}
        visible={manageAgentModalVisible}
        onClose={handlePatchAgentModalCancel}
        onPatchAgent={onPatchAgent}
        onFormDataChange={handlePatchFormDataChange}
        existingPhones={existingPhoneNumbers.filter(
          (phoneNumber) => phoneNumber !== selectedAgent?.phone_number
        )}
        existingEmails={existingEmails.filter(
          (email) => email !== selectedAgent?.email
        )}
        formData={patchAgentFormData}
      />
      <AgentAdditionMoal
        addAgentModelVisible={addAgentModelVisible}
        currentStep={currentStep}
        steps={createAgentSteps}
        formData={createAgentFormData}
        onHandleCancel={onHandleAddAgentModalCancel}
        onHandleNextStep={onHandleNextStep}
        onHandlePrevStep={onHandlePrevStep}
        isFormDataValid={isCreateAgentFormDataValid}
        setCurrentStep={setCurrentStep}
        onCreateAgent={onCreateAgent}
      />
    </div>
  );
};
export default AgentPage;
